import React from 'react';
import BudgetPhase from 'routes/BudgetModule/components/shared/BudgetLibrary/BudgetPhase';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    MANTY_ORDERED_MOVABLE_BUDGET_PHASES,
    SUPPORT,
} from 'business/constants';
import WithLoader from 'ui/WithLoader/WithLoader';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';
import MoveBudgetPhase from './MoveBudgetPhase';
import { CellRendererParams } from 'routes/BudgetModule/config/types';
import { LoadingBudgetProposalT } from 'routes/Admin/Budget/BudgetManagement/BudgetList/CellRenderers/CanResourceEditCellRenderer/gridHelpers';
import { useUserHasRole } from 'hooks/user';

const StyIcons = styled('div', {
    name: 'StyIcons',
})({
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    lineHeight: 'initial',
    marginRight: '0',
});

const StyTransparentIcons = styled(StyIcons, {
    name: 'StyTransparentIcons',
})({
    backgroundColor: 'transparent',
    border: 'transparent',
    color: 'transparent',
});

const StyBudgetPhase = styled(BudgetPhase, {
    name: 'StyBudgetPhase',
})({
    marginRight: '0',
});

export default function PhaseEditorCellRenderer({
    data,
    api,
}: CellRendererParams<LoadingBudgetProposalT>): React.ReactElement | null {
    const { t } = useTranslation();
    const isSupport = useUserHasRole(SUPPORT);
    if (!data || !api) {
        return null;
    }

    const shouldDisplayNextPhase = MANTY_ORDERED_MOVABLE_BUDGET_PHASES.filter(
        (_, index) => index < MANTY_ORDERED_MOVABLE_BUDGET_PHASES.length - 1,
    ).includes(data.phase);
    const nextPhaseIndex =
        MANTY_ORDERED_MOVABLE_BUDGET_PHASES.indexOf(data.phase) + 1;
    const nextPhase = MANTY_ORDERED_MOVABLE_BUDGET_PHASES[nextPhaseIndex];

    const shouldDisplayPreviousPhase =
        MANTY_ORDERED_MOVABLE_BUDGET_PHASES.indexOf(data.phase) >= 1;
    const previousPhaseIndex =
        MANTY_ORDERED_MOVABLE_BUDGET_PHASES.indexOf(data.phase) - 1;
    const previousPhase =
        MANTY_ORDERED_MOVABLE_BUDGET_PHASES[previousPhaseIndex];

    return (
        <Flex flexDirection="column" alignItems="center">
            <Box sx={{ lineHeight: 'initial' }}>
                <StyBudgetPhase phase={data.phase} />
            </Box>
            <WithLoader isLoading={data.isLoading ?? false}>
                {!data.isLoading ? (
                    <StyIcons>
                        {isSupport && shouldDisplayPreviousPhase && (
                            <MoveBudgetPhase
                                phase={previousPhase}
                                bpName={data.name}
                                bpId={data.id}
                                gridApi={api}
                                direction="previous"
                            />
                        )}
                        {shouldDisplayNextPhase && (
                            <MoveBudgetPhase
                                phase={nextPhase}
                                bpName={data.name}
                                bpId={data.id}
                                gridApi={api}
                                direction="next"
                            />
                        )}
                    </StyIcons>
                ) : (
                    <StyTransparentIcons>
                        {`${t('budgetProposal.moveTo')} ${t(data.phase)}`}
                    </StyTransparentIcons>
                )}
            </WithLoader>
        </Flex>
    );
}
